import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  MessageSquare,
  Plus,
  Pin,
  Users,
  ThumbsUp,
  ThumbsDown,
  MoreVertical,
  Trash2,
} from "lucide-react";
import { toast } from "react-hot-toast";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { discussionService } from "../../services/elearning/discussionService";
import { Discussion } from "../../types/elearning/discussion";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import { useAuth } from "../../hooks/useAuth";

interface CourseDiscussionBoardProps {
  courseId?: string;
}

const CourseDiscussionBoard: React.FC<CourseDiscussionBoardProps> = ({
  courseId: propCourseId,
}) => {
  const { courseId: paramCourseId } = useParams<{ courseId: string }>();
  const courseId = propCourseId || paramCourseId;
  const { user } = useAuth();
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState<string>("all");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [menuOpen, setMenuOpen] = useState<string | null>(null);
  const [newDiscussion, setNewDiscussion] = useState({
    title: "",
    content: "",
    type: "general" as const,
    category: "course_content" as const,
  });

  const menuRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const isTeacher =
    user?.roles.includes("teacher") || user?.roles.includes("branch_admin");

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch discussions
  const { data: discussionsData, isLoading } = useQuery({
    queryKey: ["course-discussions", courseId, searchQuery, filterType],
    queryFn: () => {
      const filters: any = {};
      if (searchQuery) filters.search = searchQuery;
      if (filterType !== "all") filters.type = filterType;
      return discussionService.getCourseDiscussions(courseId!, filters);
    },
    enabled: !!courseId,
  });

  // Create discussion mutation
  const createDiscussionMutation = useMutation({
    mutationFn: (data: typeof newDiscussion) =>
      discussionService.createDiscussion(courseId!, {
        title: data.title,
        description: "",
        content: data.content,
        type: data.type,
        category: data.category,
        tags: [],
        settings: {
          allowReplies: true,
          requireModeration: false,
          allowAnonymous: false,
          allowFileUploads: true,
          maxFileSize: 10 * 1024 * 1024,
          allowedFileTypes: ["pdf", "doc", "docx", "txt", "jpg", "png"],
        },
        visibility: "class_only",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["course-discussions", courseId],
      });
      setShowCreateForm(false);
      setNewDiscussion({
        title: "",
        content: "",
        type: "general",
        category: "course_content",
      });
      toast.success("Discussion created successfully!");
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.message || "Failed to create discussion"
      );
    },
  });

  // Toggle pin mutation
  const togglePinMutation = useMutation({
    mutationFn: (discussionId: string) =>
      discussionService.togglePinDiscussion(discussionId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["course-discussions", courseId],
      });
      toast.success("Discussion pin status updated");
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.message || "Failed to update pin status"
      );
    },
  });

  // Delete discussion mutation
  const deleteDiscussionMutation = useMutation({
    mutationFn: (discussionId: string) =>
      discussionService.deleteDiscussion(discussionId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["course-discussions", courseId],
      });
      toast.success("Discussion deleted successfully");
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.message || "Failed to delete discussion"
      );
    },
  });

  const handleTogglePin = (discussionId: string) => {
    togglePinMutation.mutate(discussionId);
    setMenuOpen(null);
  };

  const handleDeleteDiscussion = (discussionId: string) => {
    if (confirm("Are you sure you want to delete this discussion?")) {
      deleteDiscussionMutation.mutate(discussionId);
    }
    setMenuOpen(null);
  };

  const handleCreateDiscussion = () => {
    if (!newDiscussion.title.trim() || !newDiscussion.content.trim()) {
      toast.error("Title and content are required");
      return;
    }
    createDiscussionMutation.mutate(newDiscussion);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString();
  };

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link"],
      ["clean"],
    ],
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  const discussions = discussionsData?.data?.discussions || [];

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex-1">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center">
            <MessageSquare className="w-5 h-5 sm:w-6 sm:h-6 mr-2" />
            Course Discussions
          </h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">
            Ask questions, share ideas, and discuss course content
          </p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base w-full sm:w-auto"
        >
          <Plus className="w-4 h-4" />
          <span>New Discussion</span>
        </button>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-6">
        <div className="flex-1 relative">
          <input
            type="text"
            placeholder="Search discussions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="w-full sm:w-auto px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="all">All Types</option>
          <option value="general">General</option>
          <option value="question">Questions</option>
          <option value="announcement">Announcements</option>
        </select>
      </div>

      {/* Create Discussion Form */}
      {showCreateForm && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 mb-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">
            Create New Discussion
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title
              </label>
              <input
                type="text"
                value={newDiscussion.title}
                onChange={(e) =>
                  setNewDiscussion((prev) => ({
                    ...prev,
                    title: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter discussion title..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type
              </label>
              <select
                value={newDiscussion.type}
                onChange={(e) =>
                  setNewDiscussion((prev) => ({
                    ...prev,
                    type: e.target.value as any,
                  }))
                }
                className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="general">General Discussion</option>
                <option value="question">Question</option>
                <option value="announcement">Announcement</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Content
              </label>
              <div className="discussion-editor">
                <ReactQuill
                  value={newDiscussion.content}
                  onChange={(content) =>
                    setNewDiscussion((prev) => ({ ...prev, content }))
                  }
                  modules={quillModules}
                  theme="snow"
                  placeholder="Write your discussion content..."
                />
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleCreateDiscussion}
                disabled={createDiscussionMutation.isLoading}
                className="w-full sm:w-auto px-4 py-2 text-sm sm:text-base bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {createDiscussionMutation.isLoading
                  ? "Creating..."
                  : "Create Discussion"}
              </button>
              <button
                onClick={() => setShowCreateForm(false)}
                className="w-full sm:w-auto px-4 py-2 text-sm sm:text-base bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Discussions List */}
      <div className="space-y-4">
        {discussions.length === 0 ? (
          <div className="text-center py-12 px-4">
            <MessageSquare className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">
              No discussions yet
            </h3>
            <p className="text-sm sm:text-base text-gray-600 mb-6">
              Start the conversation by creating the first discussion for this
              course.
            </p>
            <button
              onClick={() => setShowCreateForm(true)}
              className="w-full sm:w-auto px-6 py-3 text-sm sm:text-base bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Create First Discussion
            </button>
          </div>
        ) : (
          discussions.map((discussion: Discussion) => (
            <div
              key={discussion._id}
              className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <h3
                      className="text-base sm:text-lg font-semibold text-gray-900 hover:text-blue-600 cursor-pointer break-words"
                      onClick={() =>
                        navigate(`/elearning/discussions/${discussion._id}`)
                      }
                    >
                      {discussion.title}
                    </h3>
                    {discussion.isPinned && (
                      <Pin className="w-4 h-4 text-yellow-600 flex-shrink-0" />
                    )}
                    <span
                      className={`px-2 py-1 text-xs rounded-full flex-shrink-0 ${
                        discussion.type === "question"
                          ? "bg-blue-100 text-blue-800"
                          : discussion.type === "announcement"
                          ? "bg-red-100 text-red-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {discussion.type}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-500 mb-3">
                    <span className="break-words">
                      {(discussion.authorId as any)?.name ||
                        (discussion.authorId as any)?.firstName +
                          " " +
                          (discussion.authorId as any)?.lastName ||
                        discussion.authorId}{" "}
                      •{" "}
                      {formatDate(
                        typeof discussion.createdAt === "string"
                          ? discussion.createdAt
                          : discussion.createdAt.toISOString()
                      )}
                    </span>
                    <span className="flex items-center gap-1 flex-shrink-0">
                      <Users className="w-3 h-3 sm:w-4 sm:h-4" />
                      {discussion.views}
                    </span>
                    <span className="flex items-center gap-1 flex-shrink-0">
                      <MessageSquare className="w-3 h-3 sm:w-4 sm:h-4" />
                      {discussion.replyCount}
                    </span>
                  </div>
                  <div
                    className="text-sm sm:text-base text-gray-700 line-clamp-3 break-words"
                    dangerouslySetInnerHTML={{ __html: discussion.content }}
                  />
                </div>
                <div className="flex items-center gap-2 sm:ml-4 justify-end sm:justify-start">
                  <button className="flex items-center gap-1 text-xs sm:text-sm text-gray-500 hover:text-blue-600 px-2 py-1">
                    <ThumbsUp className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span>{discussion.votes?.upVotes || 0}</span>
                  </button>
                  <button className="flex items-center gap-1 text-xs sm:text-sm text-gray-500 hover:text-red-600 px-2 py-1">
                    <ThumbsDown className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span>{discussion.votes?.downVotes || 0}</span>
                  </button>
                  {isTeacher && (
                    <div className="relative">
                      <button
                        onClick={() =>
                          setMenuOpen(
                            menuOpen === discussion._id ? null : discussion._id
                          )
                        }
                        className="p-1 sm:p-2 text-gray-400 hover:text-gray-600 rounded"
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>
                      {menuOpen === discussion._id && (
                        <div className="absolute right-0 top-8 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                          <div className="py-1">
                            <button
                              onClick={() => handleTogglePin(discussion._id)}
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                            >
                              <Pin className="w-4 h-4" />
                              <span>
                                {discussion.isPinned ? "Unpin" : "Pin"}{" "}
                                Discussion
                              </span>
                            </button>
                            <button
                              onClick={() =>
                                handleDeleteDiscussion(discussion._id)
                              }
                              className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 flex items-center space-x-2"
                            >
                              <Trash2 className="w-4 h-4" />
                              <span>Delete Discussion</span>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default CourseDiscussionBoard;
